import { useCallback } from 'react';

import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { UpdateNotificationPreferenceInput } from '@/types/schema';

import { NotificationPreferences_NotificationPreferenceFragment } from '../graphql/NotificationPreferences.generated';
import { useUpdateNotificationPreferenceMutation } from '../graphql/UpdateNotificationPreference.generated';

function buildOptimisticPreference(
  preference: NotificationPreferences_NotificationPreferenceFragment,
  patch: Record<string, unknown>
) {
  return {
    ...preference,
    ...patch,
    __typename: 'NotificationPreference' as const,
  };
}

export function usePreferenceUpdater() {
  const { createErrorFeedback } = useFeedback();
  const [mutate, { loading: saving }] = useUpdateNotificationPreferenceMutation(
    {
      onError: createErrorFeedback(
        'Unable to update notification preference. Please try again.'
      ),
    }
  );

  const update = useCallback(
    (
      preference: NotificationPreferences_NotificationPreferenceFragment,
      patch: UpdateNotificationPreferenceInput
    ) => {
      return mutate({
        variables: {
          input: {
            id: preference.id,
            update: patch,
          },
        },
        optimisticResponse: {
          updateNotificationPreference: buildOptimisticPreference(
            preference,
            patch
          ),
        },
      });
    },
    [mutate]
  );

  return { update, saving };
}
